/**
 * AI 模特图（`product-model`）的**模特设定枚举 + 逐张出图清单 —— 唯一真源**。
 *
 * 为什么单独一个文件：这些选项要同时被三处读 ——
 * 页面的选择器、提示词的【模特设定】段、核对页的摘要。
 * 选项表写两遍就会分叉（改了「体型」的档位，提示词里还是老的），
 * 这正是本项目头号 bug 类型；所以**只有这一份**，谁用谁 import。
 *
 * 选项取自公司清单「模特 / 数字人」一节的建议选项（2026-09-30 用户给的对照表）。
 * 第一项统一是「不限 / 自动」= **不指定**，不写进提示词 ——
 * 写「型号：自动」这种话给模型看只会让它自由发挥，不如不说。
 */

export type ModelLookKey =
  | 'market'
  | 'gender'
  | 'age'
  | 'body'
  | 'vibe'
  | 'hair'
  | 'pose'
  | 'scene'

export type ModelLook = Record<ModelLookKey, string>

/** 「没指定」的两种写法：市场用「不限」，其余用「自动」 */
export const MODEL_LOOK_ANY = '不限'
export const MODEL_LOOK_AUTO = '自动'

const MARKETS = ['不限', '中国', '日本', '韩国', '东南亚', '欧洲', '北美', '中东', '非洲', '拉美']
const GENDERS = ['自动', '女', '男']
const AGES = ['自动', '18-24', '25-34', '35-44', '45-54', '55+']
const BODIES = ['自动', '苗条', '标准', '健美', '丰满', '大码']
const VIBES = ['自动', '自然', '甜美', '高级', '成熟', '商务', '酷感', '活力']
const HAIRS = ['自动', '长发', '短发', '卷发', '直发', '盘发']
const POSES = ['自动', '站立', '行走', '坐姿', '回眸', '半身', '全身']
const SCENES = ['自动', '影棚', '街拍', '居家', '咖啡厅', '商务', '户外']

export type ModelLookField = {
  key: ModelLookKey
  label: string
  options: string[]
}

export type ModelLookGroup = {
  id: string
  title: string
  hint?: string
  /** 高级设置 —— 默认收起（可选项多，摊开会把必填项压下去） */
  advanced?: boolean
  fields: ModelLookField[]
}

/**
 * 分组顺序 = 页面渲染顺序 = 提示词里的书写顺序。
 * 「外貌特征」刻意夹在中间并默认收起：它是**微调**，不是每次都要填的东西。
 */
export const MODEL_LOOK_GROUPS: ModelLookGroup[] = [
  {
    id: 'basic',
    title: '模特设置',
    hint: '决定出镜的人是谁 —— 目标市场同时影响五官、肤色与穿搭风格',
    fields: [
      { key: 'market', label: '目标市场', options: MARKETS },
      { key: 'gender', label: '性别', options: GENDERS },
      { key: 'age', label: '年龄', options: AGES },
      { key: 'body', label: '体型', options: BODIES },
      { key: 'vibe', label: '模特气质', options: VIBES },
    ],
  },
  {
    id: 'look',
    title: '外貌特征',
    hint: '发型、妆容这类细节；不填就走「自动」',
    advanced: true,
    fields: [{ key: 'hair', label: '发型', options: HAIRS }],
  },
  {
    id: 'pose',
    title: '姿势与场景',
    hint: '模特怎么出镜、在哪出镜',
    fields: [
      { key: 'pose', label: '姿势', options: POSES },
      { key: 'scene', label: '场景', options: SCENES },
    ],
  },
]

/** 全部字段的扁平列表（提示词拼装、核对页摘要都用它，避免各自再遍历一遍分组） */
export const MODEL_LOOK_FIELDS: ModelLookField[] = MODEL_LOOK_GROUPS.flatMap((g) => g.fields)

export const MODEL_LOOK_DEFAULT: ModelLook = {
  market: MODEL_LOOK_ANY,
  gender: MODEL_LOOK_AUTO,
  age: MODEL_LOOK_AUTO,
  body: MODEL_LOOK_AUTO,
  vibe: MODEL_LOOK_AUTO,
  hair: MODEL_LOOK_AUTO,
  pose: MODEL_LOOK_AUTO,
  scene: MODEL_LOOK_AUTO,
}

/** 这一项算不算「用户指定了」—— 不限 / 自动都是没指定 */
export const isModelLookSet = (v: string) => Boolean(v) && v !== MODEL_LOOK_ANY && v !== MODEL_LOOK_AUTO

/**
 * 把选择拼成一段给模型看的话。全默认时返回空串 —— 调用方据此决定
 * 要不要在提示词里输出【模特设定】这一段（空段比「模特设定：不限」更干净）。
 */
export function modelLookText(look: ModelLook): string {
  return MODEL_LOOK_FIELDS.filter((f) => isModelLookSet(look[f.key]))
    .map((f) => `${f.label}：${look[f.key]}`)
    .join('；')
}

/* -------------------------------------------------------------------------- */
/* 逐张出图清单 —— 张数决定「出什么」，不是「同一种图来几张」                     */
/* -------------------------------------------------------------------------- */

/**
 * 2026-09-30 用户口径（原话）：
 *   「你应该出的图是一个正面单人照，如果用户输入 2 个的时候，出一个三视图，
 *     默认出 2 个」，随后确认档位保留 1 / 2 / 4。
 *   → 1 张 = 正面单人照；2 张（默认）= 上面那张 + 三视图；4 张 = 再换机位与场景续两张。
 *
 * ⚠️ 这一组同时纠正上一版的两个毛病：
 *
 *   ① **没有「单人单机位」这条约束** —— 实测产出把两个机位并排放进了**同一张图**
 *      （一张 1:1 里塞两格），用户原话「你应该出的图是一个正面单人照」。
 *      所以「一张图 = 一个人、一个机位」必须显式写进模板，并且是【禁止】项。
 *   ② **把「几张」当成了「同样的图来几张」** —— 第 2 张其实有独立用途：
 *      三视图是后续换装 / 复现同一张脸时的锁脸依据。
 *
 * 提示词模板 `product.product-model` 的【逐张出图清单】直接吃本函数的输出；
 * 页面正文里**不许再抄一遍这套映射**（本项目头号 bug 类型）。
 */
const SHOT_FRONT = '正面单人照 —— 正脸朝向镜头，半身或全身；画面里只有一个人、一个机位'
const SHOT_THREE_VIEW =
  '三视图 —— 正面 / 侧面 / 背面三个视角等比并排在同一张图里，同一个人、同一套光、同一背景'
const SHOT_EXTRA = '换一个机位或场景的正面单人照 —— 仍是单个人、单个机位'

/**
 * 张数 → 逐张清单。
 *
 * 序号**现算**，不在常量里写死「第 3 张」—— 档位张数一变，写死的序号就数不上。
 * 超过第 2 张之后一律续用 `SHOT_EXTRA`（用户口径：再换机位与场景续两张）。
 */
export function modelShotPlan(outputs: number): string[] {
  const n = Math.max(1, Math.floor(outputs) || 1)
  return Array.from({ length: n }, (_, i) => {
    const spec = i === 0 ? SHOT_FRONT : i === 1 ? SHOT_THREE_VIEW : SHOT_EXTRA
    return `第 ${i + 1} 张：${spec}`
  })
}

/**
 * 拼成提示词里的一行一条 —— 模板变量 `{{shotPlan}}` 的取值。
 *
 * 只有 AI 模特图这一档用（`feature.modelLook` 把关），其余档给空串、整段不输出。
 */
export function modelShotPlanText(outputs: number): string {
  return modelShotPlan(outputs)
    .map((line) => `· ${line}`)
    .join('\n')
}
